import { Controller } from "@hotwired/stimulus";

// Docs::SidebarComponent (MC-942) — the persistent docs tree browser.
//
// Two responsibilities, both driven entirely from the DOM so the controller
// survives Turbo Drive, <turbo-frame> swaps, and a Turbo cache restore:
//
//  1. Expand/collapse state — persisted per-node to localStorage keyed by
//     space + user, so a refresh (or cache restore) keeps the operator's
//     view. Applied on connect; written back on each <details> toggle.
//
//  2. Active-row highlight — the sidebar lives *outside* the `docs-main`
//     frame, so it never repaints when the main pane swaps. That means the
//     active row can't be re-rendered server-side per navigation; instead we
//     reconcile it here. On connect we honour the server-stamped active id
//     (the `activeId` value); on every `docs-main` frame load we re-read the
//     current doc id from the frame's marker and move the highlight.
//
// Lifecycle contract (docs/13-ui-conventions.md): connect() is idempotent and
// binds every listener; disconnect() removes every one it bound; no DOM
// reference is held across a navigation (targets are re-queried on use).
export default class extends Controller {
  static targets = ["node", "row"];
  static values = { storageKey: String, activeId: String };

  connect() {
    this.applyStoredState();

    this.boundToggle = this.handleToggle.bind(this);
    this.nodeTargets.forEach((node) => {
      node.addEventListener("toggle", this.boundToggle);
    });

    // Reconcile the highlight from the main pane the frame currently holds,
    // falling back to the server-stamped active id (fresh full page load).
    this.markActive(this.currentDocId() ?? this.activeIdValue);

    this.boundFrameLoad = this.handleFrameLoad.bind(this);
    document.addEventListener("turbo:frame-load", this.boundFrameLoad);
  }

  disconnect() {
    if (this.boundToggle) {
      this.nodeTargets.forEach((node) => {
        node.removeEventListener("toggle", this.boundToggle);
      });
    }
    if (this.boundFrameLoad) {
      document.removeEventListener("turbo:frame-load", this.boundFrameLoad);
    }
  }

  handleToggle(event) {
    const node = event.target;
    const id = node.dataset.nodeId;
    if (!id) return;
    const expanded = this.readSet();
    if (node.open) {
      expanded.add(id);
    } else {
      expanded.delete(id);
    }
    this.writeSet(expanded);
  }

  handleFrameLoad(event) {
    if (event.target.id !== "docs-main") return;
    this.markActive(this.currentDocId());
  }

  // The doc the main pane is currently showing, read from the marker the
  // layout stamps inside the `docs-main` frame. Null on pages with no active
  // doc (search, empty index) or when the frame isn't present (preview).
  currentDocId() {
    const frame = document.getElementById("docs-main");
    const marker = frame?.querySelector("[data-docs-current-id]");
    const id = marker?.dataset.docsCurrentId;
    return id ? String(id) : null;
  }

  // Move the highlight to the row for `id` (a string, or null/empty to clear).
  // Toggles the row modifier the CSS styles from role tokens and keeps
  // `aria-current` on exactly the active row's link.
  markActive(id) {
    const target = id ? String(id) : null;
    this.rowTargets.forEach((row) => {
      const isActive = target !== null && row.dataset.docId === target;
      row.classList.toggle("docs-sidebar__row--active", isActive);
      const link = row.querySelector(".docs-sidebar__link");
      if (!link) return;
      if (isActive) {
        link.setAttribute("aria-current", "page");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  applyStoredState() {
    const expanded = this.readSet();
    this.nodeTargets.forEach((node) => {
      const id = node.dataset.nodeId;
      if (!id) return;
      node.open = expanded.has(id);
    });
  }

  readSet() {
    if (!this.storageKeyValue) return new Set();
    try {
      const raw = window.localStorage.getItem(this.storageKeyValue);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch (_error) {
      return new Set();
    }
  }

  writeSet(set) {
    if (!this.storageKeyValue) return;
    try {
      window.localStorage.setItem(
        this.storageKeyValue,
        JSON.stringify(Array.from(set)),
      );
    } catch (_error) {
      // Quota or disabled storage — silently degrade to in-memory only.
    }
  }
}
